import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getVehicleById, deleteVehicle } from "@/services/vehicleService";
import { isAutomobile } from "@oriental-ramirez/shared";
import type { Vehicle, Brand, VehicleCategory } from "@oriental-ramirez/shared";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Car,
  Bike,
  DollarSign,
  Gauge,
  Box,
  Tag,
  Eye,
  Star,
  Calendar,
  Fuel,
  Cog,
  Shield,
  ChevronLeft,
  ChevronRight,
  Zap,
  Percent,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";
import { useState } from "react";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function toDate(val: unknown): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof (val as { toDate?: () => Date }).toDate === "function")
    return (val as { toDate(): Date }).toDate();
  if (typeof val === "object" && val !== null && "seconds" in val)
    return new Date((val as { seconds: number }).seconds * 1000);
  return null;
}

function formatDate(val: unknown): string {
  const d = toDate(val);
  if (!d) return "--";
  return d.toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatCurrency(amount: number | undefined, currency?: string): string {
  if (amount == null) return "--";
  const code = currency === "DOP" ? "DOP" : "USD";
  return new Intl.NumberFormat("es-DO", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 0,
  }).format(amount);
}

function conditionBadgeClass(condition?: string): string {
  switch (condition) {
    case "Nuevo":
      return "bg-green-100 text-green-700 border-green-300";
    case "Importada":
      return "bg-blue-100 text-blue-700 border-blue-300";
    case "Usado":
      return "bg-amber-100 text-amber-700 border-amber-300";
    case "Reacondicionado":
      return "bg-purple-100 text-purple-700 border-purple-300";
    default:
      return "";
  }
}

/* ------------------------------------------------------------------ */
/*  Spec row helper                                                   */
/* ------------------------------------------------------------------ */

function Spec({ label, value }: { label: string; value?: string | number | boolean | null }) {
  if (value == null || value === "" || value === false) return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/50 last:border-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">
        {typeof value === "boolean" ? "Sí" : String(value)}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Image gallery                                                     */
/* ------------------------------------------------------------------ */

function ImageGallery({ images }: { images: string[] }) {
  const [current, setCurrent] = useState(0);

  if (images.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="relative aspect-[16/10] overflow-hidden rounded-lg border border-border bg-muted">
        <img
          src={images[current]}
          alt=""
          className="h-full w-full object-contain"
        />
        {images.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background"
              onClick={() => setCurrent((c) => (c - 1 + images.length) % images.length)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background"
              onClick={() => setCurrent((c) => (c + 1) % images.length)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-background/80 px-2.5 py-0.5 text-xs tabular-nums">
              {current + 1} / {images.length}
            </div>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`shrink-0 h-16 w-16 rounded-md border-2 overflow-hidden transition-colors ${
                i === current ? "border-primary" : "border-transparent hover:border-muted-foreground/30"
              }`}
            >
              <img src={img} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: vehicle, isLoading } = useQuery({
    queryKey: queryKeys.vehicle(id!),
    queryFn: () => getVehicleById(id!),
    enabled: Boolean(id),
  });

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm("¿Eliminar este vehículo? Esta acción no se puede deshacer.")) return;
    try {
      await deleteVehicle(id);
      toast.success("Vehículo eliminado");
      await queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      navigate(-1);
    } catch {
      toast.error("Error al eliminar el vehículo");
    }
  };

  const isAuto = vehicle ? isAutomobile(vehicle) : false;
  const TypeIcon = isAuto ? Car : Bike;
  const typeLabel = isAuto ? "Automóvil" : "Motocicleta";
  const backUrl = isAuto ? "/vehicles/automobiles" : "/vehicles/motorcycles";
  const brand = vehicle?.brand as Brand | undefined;
  const category = vehicle?.category as VehicleCategory | undefined;
  const specs = vehicle?.specifications;
  const price = vehicle?.salePrice ?? vehicle?.minPrice ?? vehicle?.initPrice;

  /* Loading state */
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-display text-2xl font-semibold">Vehículo</h1>
        </div>
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  /* Not found state */
  if (!vehicle) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-display text-2xl font-semibold">Vehículo</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Car className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Vehículo no encontrado</p>
            <Button asChild className="mt-4">
              <Link to="/vehicles/motorcycles">Volver a vehículos</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={backUrl}>
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Volver</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl font-semibold">
                {vehicle.name ?? vehicle.model ?? "Sin nombre"}
              </h1>
              <Badge variant="outline" className="gap-1">
                <TypeIcon className="h-3 w-3" />
                {typeLabel}
              </Badge>
              {vehicle.condition && (
                <Badge variant="outline" className={conditionBadgeClass(vehicle.condition)}>
                  {vehicle.condition}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {brand?.name ?? ""}
              {vehicle.year ? ` · ${vehicle.year}` : ""}
              {category?.name ? ` · ${category.name}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" asChild>
            <Link to={`/vehicles/edit/${vehicle.id}`}>
              <Pencil className="h-4 w-4" />
              Editar
            </Link>
          </Button>
          <Button variant="outline" className="gap-2 text-destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Eliminar
          </Button>
        </div>
      </div>

      {/* ── Content grid ── */}
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Left column */}
        <div className="space-y-6">
          {/* Image gallery */}
          {(vehicle.images?.length ?? 0) > 0 && (
            <Card>
              <CardContent className="pt-6">
                <ImageGallery images={vehicle.images!} />
              </CardContent>
            </Card>
          )}

          {/* Description */}
          {vehicle.description && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Info className="h-4 w-4" />
                  Descripción
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{vehicle.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Specifications */}
          {specs && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Cog className="h-4 w-4" />
                  Especificaciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-x-8 gap-y-0 sm:grid-cols-2">
                  {/* Common specs */}
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Motor y rendimiento
                    </p>
                    <Spec label="Motor" value={specs.engineType} />
                    <Spec label="Potencia" value={specs.horsepower} />
                    <Spec label="Transmisión" value={specs.transmission} />
                    <Spec label="Vel. máxima" value={specs.maxSpeed} />
                    <Spec label="Tanque" value={specs.fuelTank} />
                    <Spec label="Consumo" value={specs.fuelConsumption} />
                    <Spec label="ABS" value={specs.abs} />
                  </div>

                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Dimensiones
                    </p>
                    <Spec label="Largo" value={specs.length} />
                    <Spec label="Ancho" value={specs.width} />
                    <Spec label="Alto" value={specs.height} />
                    <Spec label="Peso" value={specs.weight} />
                  </div>

                  {/* Motorcycle-specific */}
                  {!isAuto && (
                    <>
                      <div className="sm:col-span-2 mt-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Especificaciones de motocicleta
                        </p>
                      </div>
                      <div>
                        <Spec label="Tipo" value={specs.motorcycleType} />
                        <Spec label="Cilindrada" value={specs.cc ? `${specs.cc} cc` : undefined} />
                        <Spec label="Cilindros" value={specs.cylinders} />
                        <Spec label="Refrigeración" value={specs.cooling} />
                        <Spec label="Marchas" value={specs.gears} />
                        <Spec label="Arranque" value={specs.starter} />
                        <Spec label="Torque" value={specs.torque} />
                      </div>
                      <div>
                        <Spec label="Altura de asiento" value={specs.seatHeight} />
                        <Spec label="Distancia entre ejes" value={specs.wheelbase} />
                        <Spec label="Suspensión del." value={specs.frontSuspension} />
                        <Spec label="Suspensión tras." value={specs.rearSuspension} />
                        <Spec label="Freno del." value={specs.frontBrake} />
                        <Spec label="Freno tras." value={specs.rearBrake} />
                      </div>
                      <div className="sm:col-span-2 mt-2">
                        <Spec label="Panel digital" value={specs.digitalPanel} />
                        <Spec label="Cargador USB" value={specs.usbCharger} />
                        <Spec label="Control de tracción" value={specs.tractionControl} />
                        <Spec label="Quickshifter" value={specs.quickshifter} />
                        <Spec label="Modos de conducción" value={specs.ridingModes} />
                      </div>
                    </>
                  )}

                  {/* Automobile-specific */}
                  {isAuto && (
                    <>
                      <div className="sm:col-span-2 mt-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Especificaciones de automóvil
                        </p>
                      </div>
                      <div>
                        <Spec label="Carrocería" value={specs.bodyType} />
                        <Spec label="Puertas" value={specs.doors} />
                        <Spec label="Asientos" value={specs.seats} />
                        <Spec label="Tracción" value={specs.driveType} />
                        <Spec label="Cilindraje" value={specs.engineSize} />
                        <Spec label="Consumo ciudad" value={specs.cityConsumption} />
                        <Spec label="Consumo autopista" value={specs.highwayConsumption} />
                        <Spec label="Capacidad de carga" value={specs.cargoCapacity} />
                      </div>
                      <div>
                        <Spec label="Airbags" value={specs.airbags} />
                        <Spec label="Control estabilidad" value={specs.stabilityControl} />
                        <Spec label="Sensores de parqueo" value={specs.parkingSensors} />
                        <Spec label="Cámara reversa" value={specs.reverseCamera} />
                        <Spec label="Alerta punto ciego" value={specs.blindSpotAlert} />
                        <Spec label="Asistencia de carril" value={specs.laneAssist} />
                        <Spec label="Alerta colisión" value={specs.collisionAlert} />
                      </div>
                      <div className="sm:col-span-2 mt-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Confort y tecnología
                        </p>
                      </div>
                      <div>
                        <Spec label="Aire acondicionado" value={specs.airConditioning} />
                        <Spec label="Techo solar" value={specs.sunroof} />
                        <Spec label="Asientos de cuero" value={specs.leatherSeats} />
                        <Spec label="Asientos calefactados" value={specs.heatedSeats} />
                        <Spec label="Asientos eléctricos" value={specs.electricSeats} />
                      </div>
                      <div>
                        <Spec label="Pantalla táctil" value={specs.touchScreen} />
                        <Spec label="Navegación" value={specs.navigation} />
                        <Spec label="Bluetooth" value={specs.bluetooth} />
                        <Spec label="Puertos USB" value={specs.usbPorts} />
                        <Spec label="Sistema de sonido" value={specs.soundSystem} />
                        <Spec label="Apple CarPlay" value={specs.appleCarPlay} />
                        <Spec label="Android Auto" value={specs.androidAuto} />
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Videos */}
          {(vehicle.videos?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Videos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  {vehicle.videos!.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-md border border-border p-3 text-sm text-primary hover:bg-muted transition-colors truncate"
                    >
                      {url}
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column — sidebar cards */}
        <div className="space-y-6">
          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="h-4 w-4" />
                Precios
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {price != null && (
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    {formatCurrency(price, vehicle.currency)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Precio de venta</p>
                </div>
              )}
              <div className="border-t pt-3 space-y-1">
                <Spec label="Precio inicial" value={vehicle.initPrice != null ? formatCurrency(vehicle.initPrice, vehicle.currency) : undefined} />
                <Spec label="Precio mínimo" value={vehicle.minPrice != null ? formatCurrency(vehicle.minPrice, vehicle.currency) : undefined} />
                <Spec label="Precio dealer" value={vehicle.dealerPrice != null ? formatCurrency(vehicle.dealerPrice, vehicle.currency) : undefined} />
                <Spec label="Moneda" value={vehicle.currency ?? "USD"} />
              </div>
            </CardContent>
          </Card>

          {/* Quick info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Tag className="h-4 w-4" />
                Información general
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <Spec label="Marca" value={brand?.name} />
              <Spec label="Modelo" value={vehicle.model} />
              <Spec label="Año" value={vehicle.year} />
              <Spec label="Categoría" value={category?.name} />
              <Spec label="Condición" value={vehicle.condition} />
              <Spec label="Garantía" value={vehicle.warranty} />
              <Spec label="Kilómetros" value={vehicle.km != null ? `${vehicle.km.toLocaleString("es-DO")} km` : undefined} />
              {!isAuto && vehicle.cc && <Spec label="Cilindrada" value={`${vehicle.cc} cc`} />}
            </CardContent>
          </Card>

          {/* Inventory */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Box className="h-4 w-4" />
                Inventario
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <Spec label="Stock" value={vehicle.stock ?? vehicle.quantity ?? "--"} />
              <Spec label="Stock mínimo" value={vehicle.minStock} />
              <Spec label="Inagotable" value={vehicle.isInexhaustible} />
            </CardContent>
          </Card>

          {/* Flags & status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="h-4 w-4" />
                Estado y visibilidad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge variant={vehicle.isActive !== false ? "default" : "secondary"}>
                  {vehicle.isActive !== false ? "Activo" : "Inactivo"}
                </Badge>
                {vehicle.featured && <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">Destacado</Badge>}
                {vehicle.isHot && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Hot</Badge>}
                {vehicle.isNew && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Nuevo</Badge>}
              </div>
              <div className="mt-3 space-y-1">
                <Spec label="Vistas" value={vehicle.viewCount} />
                <Spec label="Calificación" value={vehicle.rating != null ? `${vehicle.rating} / 5` : undefined} />
                <Spec label="Reseñas" value={vehicle.reviewCount} />
              </div>
            </CardContent>
          </Card>

          {/* Special offer */}
          {vehicle.isSpecialOffer && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-orange-700">
                  <Percent className="h-4 w-4" />
                  Oferta especial
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <Spec label="Descuento" value={vehicle.specialOfferDiscount != null ? `${vehicle.specialOfferDiscount}%` : undefined} />
                <Spec label="Precio oferta" value={vehicle.specialOfferPrice != null ? formatCurrency(vehicle.specialOfferPrice, vehicle.currency) : undefined} />
                <Spec label="Fecha fin" value={formatDate(vehicle.specialOfferEndDate)} />
                <Spec label="Unidades máx." value={vehicle.specialOfferMaxUnits} />
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {(vehicle.tags?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Etiquetas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {vehicle.tags!.map((tag) => (
                    <Badge key={tag} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Creado: {formatDate(vehicle.createdAt)}
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Actualizado: {formatDate(vehicle.updatedAt)}
                </div>
                <div className="flex items-center gap-1.5 font-mono text-xs">
                  ID: {vehicle.id}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
